(() => {
  const q = (selector, context = document) => context.querySelector(selector);
  const qa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FAVORITES_KEY = 'wr-favorites-v1';
  let memoryFavorites = [];

  const readFavorites = () => {
    try {
      const value = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
      memoryFavorites = Array.isArray(value) ? [...new Set(value.filter(Boolean))] : [];
    } catch (error) {
      memoryFavorites = Array.isArray(memoryFavorites) ? memoryFavorites : [];
    }
    return memoryFavorites;
  };

  const writeFavorites = (handles) => {
    memoryFavorites = [...new Set(handles.filter(Boolean))];
    try { localStorage.setItem(FAVORITES_KEY, JSON.stringify(memoryFavorites)); } catch (error) { /* Browser privacy mode fallback. */ }
    updateFavoriteInterface(memoryFavorites);
    document.dispatchEvent(new CustomEvent('wr:favorites-change', { detail: { handles: memoryFavorites } }));
  };

  const updateFavoriteInterface = (handles = readFavorites()) => {
    const count = handles.length;
    qa('[data-favorite-count]').forEach((badge) => {
      badge.textContent = count > 0 ? String(count) : '';
      badge.hidden = count === 0;
    });
    qa('[data-favorite-toggle]').forEach((button) => {
      const active = handles.includes(button.dataset.productHandle);
      button.classList.toggle('is-favorite', active);
      button.setAttribute('aria-pressed', String(active));
      const productName = button.dataset.productTitle || '';
      button.setAttribute('aria-label', active ? productName + ' ürününü favorilerden çıkar' : productName + ' ürününü favorilere ekle');
      const label = q('span', button);
      if (label) label.textContent = active ? 'Favoride' : 'Favori';
    });
    qa('[data-favorites-page-count]').forEach((element) => { element.textContent = String(count); });
  };

  const escapeHTML = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;' }[character]));
  const safeImage = (value) => {
    if (!value) return '';
    try {
      const url = new URL(typeof value === 'string' ? value : value.src, location.origin);
      return url.protocol === 'https:' || url.origin === location.origin ? url.href : '';
    } catch (error) { return ''; }
  };
  const formatPrice = (cents) => (Number(cents || 0) / 100).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '₺';

  const activateFavoritesFallback = () => {
    if (!location.pathname.replace(/\/$/, '').endsWith('/pages/favorilerim')) return;
    const fallback = q('[data-favorites-fallback]');
    if (!fallback) return;
    fallback.hidden = false;
    const notFound = q('.wr-not-found');
    if (notFound) notFound.hidden = true;
    document.body.classList.remove('template-404');
    document.body.classList.add('template-page-favorites');
  };

  const favoriteCardMarkup = (product) => {
    const image = safeImage(product.featured_image || product.images?.[0]);
    const firstAvailable = product.variants?.find((variant) => variant.available) || product.variants?.[0];
    const handle = escapeHTML(product.handle);
    const title = escapeHTML(product.title);
    const type = escapeHTML(product.type || 'WESTER RESERVED');
    const url = '/products/' + encodeURIComponent(product.handle);
    return '<article class="wr-favorite-card" data-favorite-card="' + handle + '">'
      + '<a class="wr-favorite-card__media" href="' + url + '">' + (image ? '<img src="' + escapeHTML(image) + '" alt="' + title + '" loading="lazy" decoding="async">' : '') + '</a>'
      + '<button class="wr-favorite-card__remove" type="button" data-favorite-remove="' + handle + '" aria-label="' + title + ' ürününü favorilerden çıkar"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 4.8a5.5 5.5 0 0 0-7.8 0L12 5.9l-1.1-1.1a5.5 5.5 0 1 0-7.8 7.8l8.9 8.8 8.8-8.8a5.5 5.5 0 0 0 0-7.8Z"/></svg></button>'
      + '<div class="wr-favorite-card__info"><div><h2>' + title + '</h2><p>' + type + '</p></div><strong class="wr-favorite-card__price">' + formatPrice(product.price) + '</strong></div>'
      + '<div class="wr-favorite-card__actions"><a href="' + url + '">İNCELE</a>'
      + (firstAvailable?.available ? '<button type="button" data-quick-add data-variant-id="' + Number(firstAvailable.id) + '" aria-label="' + title + ' ürününü sepete ekle"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.5 8h11l1 12.4a1 1 0 0 1-1 1.1H6.5a1 1 0 0 1-1-1.1L6.5 8Z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg></button>' : '<button type="button" disabled>TÜKENDİ</button>')
      + '</div></article>';
  };

  const renderFavoritesPage = async () => {
    const page = q('[data-favorites-page]');
    if (!page) return;
    const grid = q('[data-favorites-grid]', page);
    const empty = q('[data-favorites-empty]', page);
    const loading = q('[data-favorites-loading]', page);
    const handles = readFavorites();
    updateFavoriteInterface(handles);
    if (!handles.length) {
      loading.hidden = true;
      empty.hidden = false;
      grid.innerHTML = '';
      return;
    }
    loading.hidden = false;
    empty.hidden = true;
    const products = (await Promise.all(handles.map(async (handle) => {
      try {
        const response = await fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } });
        return response.ok ? response.json() : null;
      } catch (error) { return null; }
    }))).filter(Boolean);
    loading.hidden = true;
    grid.innerHTML = products.map(favoriteCardMarkup).join('');
    empty.hidden = products.length > 0;
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-favorite-toggle]');
    const remove = event.target.closest('[data-favorite-remove]');
    if (!toggle && !remove) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = toggle?.dataset.productHandle || remove?.dataset.favoriteRemove;
    if (!handle) return;
    const handles = readFavorites();
    writeFavorites(handles.includes(handle) ? handles.filter((item) => item !== handle) : [...handles, handle]);
    if (remove) {
      const card = remove.closest('[data-favorite-card]');
      card?.classList.add('is-removing');
      window.setTimeout(() => {
        card?.remove();
        if (!q('[data-favorite-card]')) {
          const page = q('[data-favorites-page]');
          const empty = q('[data-favorites-empty]', page);
          if (empty) empty.hidden = false;
        }
      }, reduced ? 0 : 360);
    }
  });

  const visibleCartTarget = () => qa('[data-drawer-open="cart"]').find((element) => {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });

  const launchCartParticle = (source) => {
    const target = visibleCartTarget();
    const from = source?.getBoundingClientRect();
    if (!from) return;
    source.classList.add('is-adding-to-cart');
    window.setTimeout(() => source.classList.remove('is-adding-to-cart'), 760);
    if (reduced) {
      target?.classList.add('is-cart-arrival');
      window.setTimeout(() => target?.classList.remove('is-cart-arrival'), 480);
      return;
    }
    const to = target?.getBoundingClientRect();
    const startX = from.left + from.width / 2 - 9;
    const startY = from.top + from.height / 2 - 9;
    const endX = to ? to.left + to.width / 2 - 9 : innerWidth - 34;
    const endY = to ? to.top + to.height / 2 - 9 : 28;
    const particle = document.createElement('span');
    particle.className = 'wr-cart-particle';
    particle.innerHTML = '<b class="wr-cart-particle__orb"></b><i></i><i></i><i></i><i></i>';
    document.body.appendChild(particle);
    const flight = particle.animate([
      { transform:'translate3d(' + startX + 'px,' + startY + 'px,0) scale(.72)', opacity:0 },
      { offset:.1, transform:'translate3d(' + startX + 'px,' + startY + 'px,0) scale(1)', opacity:1 },
      { offset:.55, transform:'translate3d(' + ((startX + endX) / 2) + 'px,' + (Math.min(startY,endY) - 82) + 'px,0) scale(.88)', opacity:1 },
      { transform:'translate3d(' + endX + 'px,' + endY + 'px,0) scale(.2)', opacity:.15 }
    ], { duration:720, easing:'cubic-bezier(.42,0,.16,1)', fill:'forwards' });
    flight.finished.catch(() => {}).finally(() => particle.remove());
    window.setTimeout(() => {
      target?.classList.add('is-cart-arrival');
      window.setTimeout(() => target?.classList.remove('is-cart-arrival'), 480);
    }, 620);
  };

  document.addEventListener('click', (event) => {
    const quickAdd = event.target.closest('[data-quick-add]');
    if (quickAdd && !quickAdd.disabled) launchCartParticle(quickAdd);
  }, true);
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || event.submitter?.name === 'checkout') return;
    const button = event.submitter || q('[type="submit"]', form);
    if (button && !button.disabled) launchCartParticle(button);
  }, true);

  const initContains = (root) => {
    if (!root || root.dataset.containsReady === 'true') return;
    const slides = qa('[data-contains-slide]', root);
    const stage = q('[data-contains-stage]', root);
    const counter = q('[data-contains-counter]', root);
    if (!stage || !slides.length) return;
    root.dataset.containsReady = 'true';
    let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    const pad = (value) => String(value).padStart(2, '0');
    const show = (nextIndex) => {
      active = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const selected = index === active;
        slide.classList.toggle('is-active', selected);
        slide.setAttribute('aria-hidden', String(!selected));
      });
      if (counter) counter.textContent = pad(active + 1) + ' / ' + pad(slides.length);
    };
    q('[data-contains-prev]', root)?.addEventListener('click', (event) => { event.stopPropagation(); show(active - 1); });
    q('[data-contains-next]', root)?.addEventListener('click', (event) => { event.stopPropagation(); show(active + 1); });
    stage.addEventListener('click', (event) => {
      if (event.target.closest('a,button')) return;
      show(active + 1);
    });
    stage.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      show(active + (event.key === 'ArrowRight' ? 1 : -1));
    });
    show(active);
  };

  const initAllContains = (context = document) => {
    if (context.matches?.('[data-contains]')) initContains(context);
    qa('[data-contains]', context).forEach(initContains);
  };

  document.addEventListener('pointermove', (event) => {
    const cursor = q('.wr-cursor');
    if (!cursor) return;
    const clickStage = event.target.closest?.('[data-cursor-click]');
    const interactive = event.target.closest?.('a,button,input,select,textarea');
    cursor.classList.toggle('is-click', Boolean(clickStage && !interactive));
  });
  document.documentElement.addEventListener('mouseleave', () => q('.wr-cursor')?.classList.remove('is-click'));

  activateFavoritesFallback();
  updateFavoriteInterface();
  renderFavoritesPage();
  initAllContains();
  document.addEventListener('shopify:section:load', (event) => {
    updateFavoriteInterface();
    renderFavoritesPage();
    initAllContains(event.target);
  });
})();
